import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Building2, Home, MessageSquare, ArrowRight, Landmark } from "lucide-react";
import ChatWidget from "../components/ChatWidget";

const HERO_IMG = "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12 } }),
};

const PATHS = [
  { icon: Building2, title: "Business Funding", desc: "Working capital, equipment, and expansion capital matched to your business profile." },
  { icon: Home, title: "Real Estate Financing", desc: "Purchase, refinance, cash-out, and construction financing across property types." },
  { icon: Landmark, title: "Advisor Guidance", desc: "Talk it through with a specialist who reviews your unique situation, no pressure." },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0B0D] text-white">
      {/* Nav */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 lg:px-10">
        <Link to="/" className="flex items-center">
          <img src="/afari-logo.png" alt="AFARI Innovators LLC" className="h-14 w-auto object-contain" data-testid="brand-logo" />
        </Link>
        <Link to="/admin" data-testid="nav-admin"
          className="rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#94A3B8] transition-colors hover:border-[#C0392B]/50 hover:text-white">
          Advisor Portal
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-24 pt-10 lg:grid-cols-12 lg:px-10 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="mb-5 text-[12px] font-semibold uppercase tracking-[0.28em] text-[#C0392B]">
            AFARI Innovators · Capital Advisory
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Meet <span className="text-[#C0392B]">ARIA</span>, your funding concierge.
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#94A3B8] md:text-lg">
            A calm, informed guide for business funding and real estate financing. ARIA answers your
            questions, understands your goals, and routes you to the right AFARI pathway, without the guesswork.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-9 flex flex-col gap-3 sm:flex-row">
            <button data-testid="hero-chat-cta"
              onClick={() => document.querySelector('[data-testid="chat-launcher"]')?.click()}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C0392B] px-7 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]">
              <MessageSquare size={18} strokeWidth={2} /> Chat with ARIA
            </button>
            <a href="https://calendly.com/founder-afariinnovatorsllc" target="_blank" rel="noopener noreferrer"
              data-testid="hero-advisor-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-[#C0392B]/50">
              Speak with an advisor <ArrowRight size={16} />
            </a>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[#94A3B8]">
            <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-[#C0392B]" /> Bank-level security</span>
            <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-[#C0392B]" /> No funding guarantees implied</span>
          </motion.div>
        </div>

        {/* Hero visual */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
          className="relative lg:col-span-5">
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src={HERO_IMG} alt="Modern financial architecture" className="h-[420px] w-full object-cover" />
            <div className="absolute inset-0 bg-[#0B0B0D]/70" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <div className="rounded-2xl border border-white/15 bg-[#0B0B0D]/80 p-5 backdrop-blur-xl">
                <p className="font-display text-lg font-bold text-white">"How much can my business qualify for?"</p>
                <p className="mt-2 text-[13px] leading-relaxed text-[#94A3B8]">
                  ARIA walks you through a few quick questions, then points you to the right application, no promises, just clarity.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pathways */}
      <section className="relative z-10 border-t border-white/5 bg-[#0B0B0D]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <p className="text-[12px] font-semibold uppercase tracking-[0.28em] text-[#C0392B]">Three ways ARIA helps</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Clear paths to capital</h2>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {PATHS.map((p, i) => (
              <motion.div key={p.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="group rounded-3xl border border-white/10 bg-[#17171A] p-7 transition-transform hover:-translate-y-1">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-[#C0392B]/30 bg-[#0B0B0D]">
                  <p.icon size={22} className="text-[#C0392B]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-bold text-white">{p.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[#94A3B8]">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 px-6 py-10 lg:px-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 text-center sm:flex-row sm:text-left">
          <p className="font-display text-lg font-bold tracking-[0.2em]">AFARI<span className="text-[#C0392B]">.</span></p>
          <p className="max-w-2xl text-[11px] leading-relaxed text-[#64748B]">
            ARIA provides general information and is not a lender. Nothing here is an offer, approval, or guarantee of funding, amounts, rates, terms, or timelines.
          </p>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
